import type { IntlShape } from 'react-intl';

import type {
  FilterValues,
  SetValueProps,
} from '@src/components/FilterDropDown/FilterDropDown.interfaces';

import type {
  IWorkJourneyManagementFilterOption,
  IWorkJourneyManagementFilters,
  TWorkJourneyManagementFilterStatus,
} from './workJourneyManagementFilters';
import {
  DEFAULT_WORK_JOURNEY_MANAGEMENT_FILTERS,
  WORK_JOURNEY_MANAGEMENT_FILTER_ALL,
} from './workJourneyManagementFilters';

export const WORK_JOURNEY_MANAGEMENT_FILTER_FIELDS = {
  STATUS: 'status',
  JOURNEY_ID: 'journeyId',
  USER_ID: 'userId',
  SHIFT: 'shift',
} as const;

type OptionLookup = Map<string, string>;

/**
 * Builds FilterActiveTags `customValues` from the management filter state.
 * Keeps the existing patch-style filters as source of truth (no SecondaryPanel
 * FilterValues migration required for the list queries).
 * Month/year is navigation (not a clearable tag) — see MonthPicker in the panel.
 */
export const managementFiltersToFilterValues = (
  filters: IWorkJourneyManagementFilters,
  intl: IntlShape,
  userOptions: IWorkJourneyManagementFilterOption[],
  shiftOptions: IWorkJourneyManagementFilterOption[],
  journeyOptions: IWorkJourneyManagementFilterOption[] = [],
): FilterValues => {
  const userLabels: OptionLookup = new Map(userOptions.map(option => [option.value, option.label]));
  const shiftLabels: OptionLookup = new Map(
    shiftOptions.map(option => [option.value, option.label]),
  );
  const journeyLabels: OptionLookup = new Map(
    journeyOptions.map(option => [option.value, option.label]),
  );

  const statusLabel =
    filters.status === WORK_JOURNEY_MANAGEMENT_FILTER_ALL
      ? intl.formatMessage({ id: 'workJourney.management.filters.allStatuses' })
      : intl.formatMessage({ id: `workJourney.management.journeyStatus.${filters.status}` });

  const journeyLabel =
    filters.journeyId === WORK_JOURNEY_MANAGEMENT_FILTER_ALL
      ? intl.formatMessage({ id: 'workJourney.management.filters.allJourneys' })
      : journeyLabels.get(filters.journeyId) || filters.journeyId;

  const userLabel =
    filters.userId === WORK_JOURNEY_MANAGEMENT_FILTER_ALL
      ? intl.formatMessage({ id: 'workJourney.management.filters.allUsers' })
      : userLabels.get(filters.userId) || filters.userId;

  const shiftLabel =
    filters.shift === WORK_JOURNEY_MANAGEMENT_FILTER_ALL
      ? intl.formatMessage({ id: 'workJourney.management.filters.allShifts' })
      : shiftLabels.get(filters.shift) || filters.shift;

  return {
    [WORK_JOURNEY_MANAGEMENT_FILTER_FIELDS.STATUS]: {
      value: filters.status,
      label: intl.formatMessage({ id: 'workJourney.management.filters.status' }),
      valueTranslated: statusLabel,
      clearValue: WORK_JOURNEY_MANAGEMENT_FILTER_ALL,
      hideWhenCleared: true,
    },
    [WORK_JOURNEY_MANAGEMENT_FILTER_FIELDS.JOURNEY_ID]: {
      value: filters.journeyId,
      label: intl.formatMessage({ id: 'workJourney.management.filters.journey' }),
      valueTranslated: journeyLabel,
      clearValue: WORK_JOURNEY_MANAGEMENT_FILTER_ALL,
      hideWhenCleared: true,
    },
    [WORK_JOURNEY_MANAGEMENT_FILTER_FIELDS.USER_ID]: {
      value: filters.userId,
      label: intl.formatMessage({ id: 'workJourney.management.filters.user' }),
      valueTranslated: userLabel,
      clearValue: WORK_JOURNEY_MANAGEMENT_FILTER_ALL,
      hideWhenCleared: true,
    },
    [WORK_JOURNEY_MANAGEMENT_FILTER_FIELDS.SHIFT]: {
      value: filters.shift,
      label: intl.formatMessage({ id: 'workJourney.management.filters.shift' }),
      valueTranslated: shiftLabel,
      clearValue: WORK_JOURNEY_MANAGEMENT_FILTER_ALL,
      hideWhenCleared: true,
    },
  };
};

/** Maps a FilterActiveTags clear/change into a management filters patch. */
export const filterValueChangeToManagementPatch = (
  change: SetValueProps,
): Partial<IWorkJourneyManagementFilters> => {
  switch (change.key) {
    case WORK_JOURNEY_MANAGEMENT_FILTER_FIELDS.STATUS: {
      const status =
        typeof change.value === 'string' && change.value
          ? (change.value as TWorkJourneyManagementFilterStatus | typeof WORK_JOURNEY_MANAGEMENT_FILTER_ALL)
          : DEFAULT_WORK_JOURNEY_MANAGEMENT_FILTERS.status;
      return { status };
    }
    case WORK_JOURNEY_MANAGEMENT_FILTER_FIELDS.JOURNEY_ID: {
      const journeyId =
        typeof change.value === 'string' && change.value
          ? change.value
          : DEFAULT_WORK_JOURNEY_MANAGEMENT_FILTERS.journeyId;
      return { journeyId };
    }
    case WORK_JOURNEY_MANAGEMENT_FILTER_FIELDS.USER_ID: {
      const userId =
        typeof change.value === 'string' && change.value
          ? change.value
          : DEFAULT_WORK_JOURNEY_MANAGEMENT_FILTERS.userId;
      return { userId };
    }
    case WORK_JOURNEY_MANAGEMENT_FILTER_FIELDS.SHIFT: {
      const shift =
        typeof change.value === 'string' && change.value
          ? change.value
          : DEFAULT_WORK_JOURNEY_MANAGEMENT_FILTERS.shift;
      return { shift };
    }
    default:
      return {};
  }
};

/** Restores management filter state from persisted SecondaryPanel FilterValues. */
export const filterValuesToManagementFilters = (
  values: FilterValues,
): IWorkJourneyManagementFilters => {
  const statusValue = values[WORK_JOURNEY_MANAGEMENT_FILTER_FIELDS.STATUS]?.value;
  const journeyValue = values[WORK_JOURNEY_MANAGEMENT_FILTER_FIELDS.JOURNEY_ID]?.value;
  const userValue = values[WORK_JOURNEY_MANAGEMENT_FILTER_FIELDS.USER_ID]?.value;
  const shiftValue = values[WORK_JOURNEY_MANAGEMENT_FILTER_FIELDS.SHIFT]?.value;

  return {
    status:
      typeof statusValue === 'string' && statusValue
        ? (statusValue as TWorkJourneyManagementFilterStatus | typeof WORK_JOURNEY_MANAGEMENT_FILTER_ALL)
        : DEFAULT_WORK_JOURNEY_MANAGEMENT_FILTERS.status,
    journeyId:
      typeof journeyValue === 'string' && journeyValue
        ? journeyValue
        : DEFAULT_WORK_JOURNEY_MANAGEMENT_FILTERS.journeyId,
    userId:
      typeof userValue === 'string' && userValue
        ? userValue
        : DEFAULT_WORK_JOURNEY_MANAGEMENT_FILTERS.userId,
    shift:
      typeof shiftValue === 'string' && shiftValue
        ? shiftValue
        : DEFAULT_WORK_JOURNEY_MANAGEMENT_FILTERS.shift,
  };
};
